import styles from "../InternationalCollaboratorMap/InternationalCollaboratorMap.module.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5geodata_usaLow from "@amcharts/amcharts5-geodata/usaLow";
import { Select, Spin } from "antd";
import StateCollaboratorList from "./StateCollaboratorList";
import { IconButton, Modal, Typography } from "@mui/material";
import useDeviceCheck from "Hooks/useDeviceCheck";
import { CloseOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { TIME_FILTER_OPTIONS } from "Explore/Config/constraint";
import { AutoCompleteFilters } from "Explore/Components";
import { getSuggestKeywords } from "Explore/Services/Common";
import { ParamsFilterKey } from "Config/filterSearchParams";

const USCollaboratorMap = ({
  data,
  isLoading,
  metric = "publications",
  selectedKeywords,
  setSelectedKeywords,
  timeFilter,
  setTimeFilter,
  fetchFunction,
  disableHoverCover = false,
  width = "100%",
  height: _height,
}) => {
  const { isMobile } = useDeviceCheck();
  const chartRef = useRef(null);
  const [selectedState, setSelectedState] = useState(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const height = _height || (isMobile ? "330px" : "700px");
  const mainColor = useMemo(() => {
    if (typeof window !== "undefined") {
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue("--explore-color")
        .trim();
      if (value) return value;
    }
    return "#0f3460";
  }, []);

  const getMetricDataKey = () => {
    switch (metric) {
      case "fwci": return "fwci";
      case "citations": return "citations";
      case "collaborators": return "collaborators";
      case "candidates": return "value";
      case "publications":
      default: return "num_pubs";
    }
  };

  const getMetricLabel = () => {
    switch (metric) {
      case "fwci": return "FWCI";
      case "citations": return "Citations";
      case "collaborators": return "Collaborators";
      case "candidates": return "Candidates";
      case "publications":
      default: return "Publications";
    }
  };

  const convertData = (data) => {
    const dataKey = getMetricDataKey();
    const metricLabel = getMetricLabel();
    const maxScore = Math.max(...data.map((d) => d[dataKey] || 0));
    const hsl = d3.hsl(d3.rgb(mainColor));

    return data.map((d) => {
      const score = (d[dataKey] || 0) / (maxScore || 1);
      const safeScore = isFinite(score) ? score : 0;
      const lightness = 0.9 * (1 - safeScore) + hsl.l * safeScore;

      return {
        ...d,
        id: d.state_code, // REQUIRED for mapping to GeoJSON
        polygonSettings: {
          tooltipText: `{name}: {${dataKey}} ${metricLabel}`,
          fill: am5.color(d3.hsl(hsl.h, hsl.s, lightness).toString()),
          interactive: true,
        },
      };
    });
  };

  useEffect(() => {
    if (!!selectedState && !!data.length) {
      const newSelectedState = convertData(data).find(
        (d) => d.id === selectedState.id
      );
      if (!!newSelectedState)
        setSelectedState({
          ...selectedState,
          common_keywords: newSelectedState?.common_keywords,
          num_pubs: newSelectedState?.num_pubs,
        });
    }
  }, [data]);

  useEffect(() => {
    let root;
    root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: "translateX",
        panY: "translateY",
        projection: am5map.geoAlbersUsa(),
      })
    );

    const zoomControl = chart.set(
      "zoomControl",
      am5map.ZoomControl.new(root, {})
    );
    zoomControl.homeButton.set("visible", true);

    const polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {
        geoJSON: am5geodata_usaLow,
      })
    );

    polygonSeries.mapPolygons.template.setAll({
      fill: am5.color("#d9d9d9"),
      tooltipText: "{name}",
      templateField: "polygonSettings",
      interactive: true,
      stroke: am5.color("#ffffff"),
      strokeWidth: 0.5,
      cursorOverStyle: "pointer",
    });

    polygonSeries.mapPolygons.template.events.on("click", (ev) => {
      const dataContext = ev.target.dataItem.dataContext;
      const geo = dataContext?.geometry;

      if (!geo) return;

      if (!!dataContext.polygonSettings) {
        setSelectedState(dataContext);
      }

      const centroid = am5map.getGeoCentroid(geo);
      if (!centroid) return;

      const offsetGeoPoint = {
        longitude: centroid.longitude,
        latitude: centroid.latitude,
      };
      chart.zoomToGeoPoint(offsetGeoPoint, 4, true);
    });

    polygonSeries.mapPolygons.template.states.create("hover", {
      fill: am5.color(mainColor),
    });

    polygonSeries.data.setAll(convertData(data));
    chart.appear(100, 100);

    return () => {
      if (root) {
        root.dispose();
      }
    };
  }, [data, isLoading, mainColor]);

  return (
    <>
      <div className={styles["container"]} style={{ width, height }}>
        {isLoading && (
          <div className={styles["loading"]}>
            <Spin />
          </div>
        )}
        {isMobile && !disableHoverCover && (
          <Typography variant='subtitle2'>Click to view more details</Typography>
        )}
        <div
          className={classNames(styles["keyword-search"], {
            [styles["hidden"]]: !disableHoverCover,
          })}
        >
          <div style={{ flex: 1 }}>
            <AutoCompleteFilters
              placeholder='Enter research topics'
              functionSearch={getSuggestKeywords}
              filters={{ [ParamsFilterKey.KEYWORD_QUERY]: selectedKeywords }}
              setFilters={(update) =>
                setSelectedKeywords(update?.[ParamsFilterKey.KEYWORD_QUERY] || [])
              }
              entity={ParamsFilterKey.KEYWORD_QUERY}
              getFirstItemOnEnter
            />
          </div>
          <Select
            value={timeFilter}
            style={{ width: 130 }}
            onChange={(value) => setTimeFilter(value)}
            options={TIME_FILTER_OPTIONS}
          />
        </div>
        <div ref={chartRef} className={styles["chart"]}></div>
        {!isLoading && !disableHoverCover && (
          <div
            className={styles["hover-cover"]}
            onClick={() => setModalOpen(true)}
          >
            <Typography variant='body1' component='p'>
              Click to view more details
            </Typography>
          </div>
        )}
        <StateCollaboratorList
          data={selectedState}
          setData={setSelectedState}
          fetchFunction={fetchFunction}
          selectedKeywords={selectedKeywords}
          setSelectedKeywords={setSelectedKeywords}
          timeFilter={timeFilter}
          setTimeFilter={setTimeFilter}
        />
      </div>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        style={{ zIndex: 1029 }}
      >
        <div className={styles["modal"]}>
          <IconButton
            onClick={() => setModalOpen(false)}
            className={styles["close-button"]}
          >
            <CloseOutlined />
          </IconButton>
          <USCollaboratorMap
            data={data}
            metric={metric}
            isLoading={isLoading}
            disableHoverCover
            height={"100%"}
            fetchFunction={fetchFunction}
            selectedKeywords={selectedKeywords}
            setSelectedKeywords={setSelectedKeywords}
            timeFilter={timeFilter}
            setTimeFilter={setTimeFilter}
          />
        </div>
      </Modal>
    </>
  );
};

export default USCollaboratorMap;
